import {createRouter, createWebHistory} from 'vue-router';
import { usePostHog } from '@/composables/usePostHog'
import NotFoundView from '@/views/NotFoundView.vue';
import { useAuthStore } from '@/stores/auth';
import AgentPortalLayout from "@/layouts/AgentPortalLayout.vue";
import PublicLayout from '@/layouts/PublicLayout.vue';
import AdministrationPortalLayout from "@/layouts/AdministrationPortalLayout.vue";

const routes = [
    {
        path: '/',
        component: PublicLayout, // Use PublicLayout as the "master page" for public routes
        meta: { requiresAuth: false, isPublicParent: true },
        children: [
            {
                path: '', // Empty path matches '/' exactly
                name: 'home',
                component: () => import('@/views/HomeView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'product',
                name: 'product',
                component: () => import('@/views/ProductView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'price',
                name: 'price',
                component: () => import('@/views/PricingView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'contact-us',
                name: 'contact-us',
                component: () => import('@/views/ContactUsView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'signup',
                name: 'signup',
                component: () => import('@/views/SignUpView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'login',
                name: 'login',
                component: () => import('@/views/LoginView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'login/admin',
                name: 'admin-login',
                component: () => import('@/views/LoginAdminView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'terms-of-service',
                name: 'terms-of-service',
                component: () => import('@/views/TermsOfServiceView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'privacy-policy',
                name: 'privacy-policy',
                component: () => import('@/views/PrivacyPolicyView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'refund-policy',
                name: 'refund-policy',
                component: () => import('@/views/RefundPolicyView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'verify-email',
                name: 'verify-email',
                component: () => import('@/views/VerifyEmailView.vue'),
                meta: { requiresAuth: false }
            },
            {
                path: 'no-access',
                name: 'no-access',
                component: () => import('@/views/NoAccessView.vue'),
                meta: { requiresAuth: false }
            }
        ],
    },

    // Agent portal: set auth and portalAccess at parent so children don't need repeating meta
    {
        path: '/portal/agent',
        component: AgentPortalLayout,
        meta: { requiresAuth: true, portalAccess: ['agent'] },
        children: [
            {
                path: 'user-profile/:id',
                name: 'agent-user-profile',
                component: () => import('@/views/portal/agent/UserProfileView.vue')
            },
            {
                path: 'dashboard',
                name: 'agent-dashboard',
                component: () => import('@/views/portal/agent/DashboardView.vue')
            },
            {
                path: 'manage-listings',
                name: 'agent-manage-listings',
                component: () => import('@/views/portal/agent/ManageListingsView.vue')
            },
            {
                path: 'groups',
                name: 'agent-network-groups',
                component: () => import('@/views/portal/agent/GroupsView.vue')
            },
            {
                path: 'colleagues',
                name: 'agent-network-colleagues',
                component: () => import('@/views/portal/agent/ColleaguesView.vue')
            },
            {
                path: 'user-guide',
                name: 'agent-user-guide',
                component: () => import('@/views/portal/agent/UserGuideView.vue')
            }
        ],
    },

    // Empty placeholder portals for future use
    {
        path: '/portal/developer',
        component: AgentPortalLayout,
        meta: { requiresAuth: true, portalAccess: ['developer'] },
        children: []
    },
    {
        path: '/portal/brokerage',
        component: AgentPortalLayout,
        meta: { requiresAuth: true, portalAccess: ['brokerage'] },
        children: []
    },
    {
        path: '/portal/unit-owner',
        component: AgentPortalLayout,
        meta: { requiresAuth: true, portalAccess: ['unit-owner'] },
        children: []
    },
    {
        path: '/portal/administration',
        component: AdministrationPortalLayout,
        meta: { requiresAuth: true, portalAccess: ['admin'] },
        children: [
            {
                path: 'dashboard',
                name: 'admin-dashboard',
                component: () => import('@/views/portal/administration/DashboardView.vue')
            },
        ]
    },

    {
        path: '/:pathMatch(.*)*', // Catch-all route for 404
        name: 'not-found',
        component: NotFoundView,
        meta: { requiresAuth: false }
    },
];


const router = createRouter({
    history: createWebHistory('/'),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        return { top: 0 };
    }
});

router.onError((error) => {
    console.error('Router error:', error);
    if (error.message.includes('Failed to fetch dynamically imported module')) {
        window.location.reload();
    }
});


router.beforeEach(async (to) => {
    const auth = useAuthStore();

    // --- Root path ---
    if (to.path === '/') return true;

    // --- Public routes ---
    const isPublic = to.matched.some((r) => r.meta.requiresAuth === false);
    if (isPublic) {
        if (auth.user && (to.name === 'login' || to.name === 'signup')) {
            return { name: 'dashboard' };
        }
        return true;
    }

    // --- Auth check ---
    const isAuthenticated = auth.user ? true : await auth.fetchUser();

    if (!isAuthenticated) {
        return { name: 'login', query: { redirect: to.fullPath } };
    }

    // --- Collect meta requirements (handles nested routes properly) ---
    const portalAccess = to.matched.flatMap(r => r.meta.portalAccess || []);

    // --- Portal access check (portalAccess must match one of the allowed portalAccess entries) ---
    console.log('portalAccess:', portalAccess);
    if (
        portalAccess.length &&
        (!auth.user?.portalAccess || !portalAccess.includes(auth.user.portalAccess))
    ) {
        return {
            name: 'invalid-access-level'
        };
    }

    return true;
});

const { posthog } = usePostHog()
export default router;
